<template>
  <v-card color="#111827">
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      <v-toolbar-title>Analyse der Ausgaben</v-toolbar-title>
    </v-toolbar>

    <v-card-text class="pt-4">
      <v-tabs v-model="currentView" background-color="transparent" dark grow>
        <v-tab href="#chart">
          <v-icon left>mdi-chart-bar</v-icon>
          Grafische Auswertung
        </v-tab>
        <v-tab href="#table">
          <v-icon left>mdi-table</v-icon>
          Tabellarische Ansicht
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="currentView" class="transparent mt-4">
        <v-tab-item value="chart">
          <v-card color="#1F2937" class="pa-4 rounded-lg">
            <v-select
              v-model="selectedStudio"
              :items="studioSelectItems"
              label="Studio auswählen"
              outlined
              dense
              dark
              hide-details
              class="mb-4"
              style="max-width: 300px"></v-select>

            <apexchart height="500" type="bar" :series="chartSeries" :options="chartOptions"></apexchart>
          </v-card>
        </v-tab-item>
        <v-tab-item value="table">
          <v-card color="#1F2937" class="pa-2 rounded-lg">
            <v-data-table :headers="tableHeaders" :items="flatInvoiceList" :search="search" dark class="elevation-0 transparent">
              <template v-slot:top>
                <v-text-field
                  v-model="search"
                  label="Rechnungen durchsuchen..."
                  class="mx-4"
                  dark
                  clearable
                  outlined
                  dense
                  hide-details></v-text-field>
              </template>
              <template v-slot:item.date="{ item }">
                {{ new Date(item.date).toLocaleDateString("de-CH") }}
              </template>
              <template v-slot:item.invoiceIssuer="{ item }">
                {{ item.invoiceIssuer || "Unbekannt" }}
              </template>
              <template v-slot:item.amount="{ item }">
                <span class="font-weight-bold">{{ formatCurrency(item.amount) }}</span>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "InvoiceAnalysis",
  props: {
    stats: { type: Object, required: true },
  },
  data() {
    return {
      currentView: "chart",
      search: "",
      // NEU: State für das ausgewählte Studio
      selectedStudio: "Gesamt", // Standardwert
      tableHeaders: [
        { text: "Studio", value: "studioName" },
        { text: "Datum", value: "date" },
        { text: "Titel / Rechnungssteller", value: "invoiceIssuer" },
        { text: "Rechnungsnummer", value: "invoiceNumber" },
        { text: "Betrag", value: "amount", align: "end" },
      ],
    }
  },
  methods: {
    formatCurrency(value) {
      if (typeof value !== "number") return "0.00 CHF"
      return `${value.toFixed(2)} CHF`
    },
  },
  computed: {
    flatInvoiceList() {
      if (!this.stats?.detailedInvoicesPerStudio) return []
      return this.stats.detailedInvoicesPerStudio.flatMap((studioGroup) =>
        studioGroup.invoices.map((invoice) => ({
          ...invoice,
          studioName: studioGroup.studioName,
          invoiceIssuer: invoice.invoiceIssuer || invoice.title,
        }))
      )
    },

    // NEU: Erstellt die Liste für das Dropdown-Menü
    studioSelectItems() {
      if (!this.stats?.monthlyTotalsPerStudio) return ["Gesamt"]
      const studioNames = this.stats.monthlyTotalsPerStudio.map((s) => s.studioName)
      return ["Gesamt", ...studioNames.sort()]
    },

    // KORRIGIERT: chartSeries ist jetzt dynamisch basierend auf der Auswahl
    chartSeries() {
      if (!this.stats?.monthlyTotalsPerStudio) return []

      // Fall 1: "Gesamt" ist ausgewählt -> Summiere die Daten aller Studios
      if (this.selectedStudio === "Gesamt") {
        const monthlyTotals = new Array(12).fill(0)
        this.stats.monthlyTotalsPerStudio.forEach((studio) => {
          studio.data.forEach((monthData) => {
            monthlyTotals[monthData.month - 1] += monthData.total
          })
        })
        return [{ name: "Gesamtausgaben", data: monthlyTotals }]
      }

      // Fall 2: Ein spezifisches Studio ist ausgewählt
      const studioData = this.stats.monthlyTotalsPerStudio.find((s) => s.studioName === this.selectedStudio)

      if (!studioData) return []

      const monthlyData = new Array(12).fill(0)
      studioData.data.forEach((monthData) => {
        monthlyData[monthData.month - 1] = monthData.total
      })

      return [{ name: studioData.studioName, data: monthlyData }]
    },
    chartOptions() {
      return {
        chart: { type: "bar", foreColor: "#9CA3AF", toolbar: { show: true } },
        plotOptions: { bar: { horizontal: false, columnWidth: "75%", dataLabels: { position: "top" } } },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
          categories: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
        },
        yaxis: { title: { text: "Ausgaben (CHF)" }, labels: { formatter: (val) => this.formatCurrency(val) } },
        fill: { opacity: 1 },
        tooltip: { theme: "dark", y: { formatter: (val) => this.formatCurrency(val) } },
        grid: { borderColor: "#374151" },
        legend: { position: "top", horizontalAlign: "left" },
      }
    },
  },
}
</script>
